<template>
  <div>
    <div :class="$style['address-chooser']">
      <div :class="$style['address-search']">
        <v-text-field
          v-model="search"
          :class="$style.search"
          append-icon="mdi-magnify"
          :label="$t('checkout.searchLabel')"
          single-line
          hide-details
          clearable
        ></v-text-field>
      </div>
      <v-sheet
        :max-height="maxHeight"
        :class="maxHeight ? 'overflow-y-auto overflow-x-hidden pl-3' : ''"
      >
        <v-radio-group v-model="selectedDeliveryAddress" row hide-details :disabled="disabled">
          <v-radio
            v-for="address in filteredAdresses"
            :key="address.id"
            color="black"
            :value="address.id"
            required
            class="w-100 mb-5"
          >
            <template #label>
              <ul
                :class="[
                  $style.list,
                  'unstyled-list',
                  { [$style.hasContact]: address.hasContactId },
                ]"
              >
                <li v-for="adrPart in address.formatted" :key="adrPart">{{ adrPart }}</li>
              </ul>
            </template>
          </v-radio>
        </v-radio-group>
      </v-sheet>
    </div>
  </div>
</template>

<script>
import { get, isUndefined } from 'lodash-es'
import { mapGetters } from 'vuex'
import textSearch from 'simple-text-search'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import GETTER_TYPES from '@/js/plugins/store/modules/aimeos/getter-types'
import API_CONSTANTS from '@/js/helper/api-constants.js'

export default {
  name: 'DeliveryAddressSearch',
  props: {
    currentAddressId: {
      type: [String, Number],
      default: undefined,
    },
    maxHeight: {
      type: Number,
      default: undefined,
    },
    addresses: {
      type: Array,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedDeliveryAddress: undefined,
      search: '',
    }
  },
  computed: {
    ...mapGetters(STOREMODULES.AIMEOS, {
      deliveryAddressesAimeos: GETTER_TYPES.CUSTOMER_DELIVERY_ADDRESSES_FORMATTED,
    }),
    deliveryAddresses() {
      return this.addresses ? this.addresses : this.deliveryAddressesAimeos
    },
    filterWords() {
      return this.deliveryAddresses.reduce((acc, item) => {
        const id = get(item, API_CONSTANTS.ID)
        const words = get(item, 'formatted', []).join(' ')
        acc.push({ id, words })
        return acc
      }, [])
    },
    filter() {
      return textSearch(this.filterWords, 'words')
    },
    filteredAdresses() {
      if (!this.search) {
        return this.deliveryAddresses
      }
      const results = this.filter(this.search).map(res => res.id)
      return this.deliveryAddresses.filter(
        address => address.id === this.selectedDeliveryAddress || results.includes(address.id),
      )
    },
  },
  watch: {
    selectedDeliveryAddress() {
      if (isUndefined(this.selectedDeliveryAddress)) {
        return
      }
      this.setDeliveryAddress(this.selectedDeliveryAddress)
    },
    deliveryAddresses: {
      handler() {
        this.selectedDeliveryAddress =
          this.currentAddressId || get(this.deliveryAddresses, [0, 'id'])
      },
      immediate: true,
    },
    currentAddressId() {
      this.selectedDeliveryAddress = this.currentAddressId || get(this.deliveryAddresses, [0, 'id'])
    },
  },
  methods: {
    setDeliveryAddress() {
      if (this.currentAddressId === this.selectedDeliveryAddress) {
        return
      }
      this.$emit('set-address', this.selectedDeliveryAddress)
    },
  },
}
</script>

<style lang="scss" module>
.address-chooser {
  :global(.v-input--selection-controls .v-input__control) {
    width: 100%;
  }

  :global(.v-input--radio-group__input) {
    width: calc(100% + 3rem);
    margin-left: -3rem;
  }

  :global(.v-radio) {
    align-items: flex-start !important;
    margin-right: 0;
    margin-left: 3rem;
  }
}

.address-search {
  position: sticky;
  top: 0px;
  z-index: 1;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 0.875rem;
  padding-bottom: 1rem;

  color: var(--grey-lighten-1);

  background: var(--white);

  :global(.v-label) {
    color: var(--grey-lighten-1) !important;
  }

  :global(.v-input__slot::before),
  :global(.v-input__slot::after) {
    bottom: -0.5rem !important;

    border-color: var(--primary) !important;
  }

  :global(.v-input__slot::before),
  :global(.v-input__slot:hover::before) {
    border-color: var(--primary) !important;
    border-width: 1px !important;
  }

  :global(.v-label) {
    height: 1.625rem;
  }

  :global(.v-label--active) {
    color: var(--grey-lighten-1);
  }

  :global(.v-input__append-inner) {
    margin-top: 0.75rem !important;
  }

  :global(.v-icon) {
    color: var(--primary) !important;
  }
}

:global(.unstyled-list).list {
  font-weight: var(--font-weight-normal);
  line-height: 1.6;

  li:first-child {
    font-weight: var(--font-weight-bold);
  }
}

.hasContact {
  margin-left: -7px !important;
  padding-left: 5px !important;

  border-left: 2px solid var(--primary);
}
</style>
